import { Box, Button, Card, Container, Grid, Title } from '@mantine/core';
import { IconCloudDownload, IconUser } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import DatePicker from '../../components/DatePicker';
import ErrorMessage from '../../components/ErrorMessage';
import EventSelector from '../../components/EventSelector';
import Metadata from '../../components/Metadata';
import Pager from '../../components/Pager/Pager';
import ProfileEvents from '../../components/Profiles/ProfileEvents';
import CustomFieldExportModal from '../../components/Modals/CustomFieldExportModal';
import Spinner from '../../components/Spinner';
import PageSizeOptions from '../../constants/pageSizeOptions';
import { Page } from '../../constants/pageTitles';
import useEventTypes from '../../helpers/useEventTypes';
import useFetch from '../../helpers/useFetch';

const fetchDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, filter) =>
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/events?${queryString.stringify(filter, {
        arrayFormat: 'bracket',
      })}`
    ),
  300
);

const startTime = new Date();
startTime.setTime(startTime.getTime() - 30 * 24 * 3600 * 1000);
startTime.setHours(0);
startTime.setMinutes(0);
startTime.setSeconds(0);
startTime.setMilliseconds(0);

const filterDefaultValues = {
  from: 0,
  size: PageSizeOptions[0],
  startTime: startTime.toISOString(),
};

const EventsPage = () => {
  const location = useLocation();
  const fetch = useFetch();
  const navigate = useNavigate();

  const [filter, setFilter] = useState(filterDefaultValues);
  const [events, setEvents] = useState();
  const [totalCount, setTotalCount] = useState();
  const [pageCount, setPageCount] = useState();
  const [exportState, setExportState] = useState();
  const [customFieldModalOpen, setCustomFieldModalOpen] = useState(false);
  const [segments, setSegments] = useState();
  const [error, setError] = useState();

  const { accountId, domainId } = useParams();
  const eventTypes = useEventTypes(accountId, domainId, setError);

  const parsedSearch = useMemo(() => {
    const parsed = queryString.parse(location.search, { arrayFormat: 'bracket' });
    return { ...filterDefaultValues, ...parsed };
  }, [location.search]);

  useEffect(() => {
    setFilter(parsedSearch);
    setEvents(undefined);
    fetchDebounced(fetch, accountId, domainId, parsedSearch).then(({ events, totalCount }) => {
      setEvents(events);
      setTotalCount(totalCount);
      setPageCount(Math.ceil(totalCount / filter.size));
    });
  }, [accountId, domainId, fetch, parsedSearch, filter.size]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments?scope=behavior`)
      .then(({ segments }) => {
        setSegments(
          segments.map((s) => {
            return { value: s.id.toString(), label: s.name };
          })
        );
      })
      .catch(setError);
  }, [fetch, accountId, domainId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFilter = { ...filter, [name]: value, from: 0 };

    navigate({ search: queryString.stringify({ ...newFilter }, { arrayFormat: 'bracket' }) });
    setFilter(newFilter);
  };

  const handlePageClick = (e) => {
    const selectedPage = e.selected;
    const newOffset = (selectedPage - 1) * filter.size;
    if (newOffset !== parsedSearch.from) {
      navigate({
        search: queryString.stringify(
          { ...parsedSearch, from: (selectedPage - 1) * parseInt(filter.size) },
          { arrayFormat: 'bracket' }
        ),
      });
    }
  };

  const setPageSize = async (pageSize) => {
    navigate({
      search: queryString.stringify({ ...parsedSearch, from: 0, size: pageSize }, { arrayFormat: 'bracket' }),
    });
  };

  const getDate = (date) => {
    if (!date) {
      return;
    }
    return new Date(date);
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!segments) {
    return <Spinner />;
  }

  const createExport = (customFields) => {
    setExportState('pending');
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/events/exports`, {
      method: 'POST',
      body: JSON.stringify({ ...queryString.parse(location.search, { arrayFormat: 'bracket' }), customFields }),
    })
      .then(() => {
        setExportState('success');
      })
      .catch((error) => {
        setError(error);
        setExportState('error');
      });
  };

  const common = (
    <>
      <Metadata title={`${Page['events']}`} />
      <Card withBorder mb="sm">
        <Card.Section inheritPadding py="md" withBorder>
          <Container size="xl">
            {eventTypes ? (
              <form>
                <Grid mb="md" justify="center">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <EventSelector
                      isMulti={true}
                      actions={parsedSearch?.eventFilter || []}
                      setActions={(selected) => handleChange({ target: { name: 'eventFilter', value: selected } })}
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Grid>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <DatePicker
                          prepend="From"
                          showTimeSelect
                          placeholder="From date"
                          value={parsedSearch.startTime}
                          onChange={(value) => handleChange({ target: { name: 'startTime', value } })}
                          maxDate={getDate(parsedSearch.endTime)}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <DatePicker
                          prepend="to"
                          append="local time"
                          showTimeSelect
                          placeholder="To date"
                          value={parsedSearch.endTime}
                          onChange={(value) => handleChange({ target: { name: 'endTime', value } })}
                          minDate={getDate(parsedSearch.startTime)}
                        />
                      </Grid.Col>
                    </Grid>
                  </Grid.Col>
                </Grid>
              </form>
            ) : (
              <Spinner />
            )}
          </Container>
        </Card.Section>
      </Card>
    </>
  );

  if (!events) {
    return (
      <>
        {common}
        <Spinner />
      </>
    );
  }

  let exportButton = '';
  if (events.length > 0) {
    exportButton = (
      <Button
        variant="default"
        onClick={() => setCustomFieldModalOpen(true)}
        disabled={exportState === 'pending' || exportState === 'success'}
      >
        <IconCloudDownload size={16} />{' '}
        {exportState === 'success' ? 'Export scheduled. Please check your email for a download link.' : 'Export'}
      </Button>
    );
  }

  return (
    <>
      {common}
      <Card withBorder mb="sm">
        <Card.Section inheritPadding py="md">
          {totalCount !== undefined ? (
            <Box style={{ float: 'left' }}>
              <Title order={5}>
                <IconUser size={16} /> {totalCount.toLocaleString(navigator.language)} Events
              </Title>
            </Box>
          ) : (
            'Loading...'
          )}
          <Box style={{ float: 'right' }}>{exportButton}</Box>
        </Card.Section>
        <ProfileEvents events={events} showProfileLink={true} eventTypes={eventTypes} />
      </Card>
      <Pager
        pageSize={parseInt(filter.size)}
        pageCount={pageCount}
        onPageChange={handlePageClick}
        initialPage={Math.round(parseInt(parsedSearch.from || '0') / parseInt(filter.size) + 1)}
        setPageSize={setPageSize}
      />
      <CustomFieldExportModal
        show={customFieldModalOpen}
        close={() => setCustomFieldModalOpen(false)}
        onExport={createExport}
        fieldScope="event"
        title="Export Events with Custom Fields"
        description="Select event custom fields to include as separate columns in your export. Standard columns (name, email, phone, action, order, timestamp, etc.) will always be included."
      />
    </>
  );
};

export default EventsPage;
